import { useState } from "react";
import { useTranslation } from "react-i18next";
import { Alert, Button, useToast } from "@/core/ui";
import { usePuzzleTypes, useExportPuzzle, type UnsupportedItem } from "./api";

function download(filename: string, content: string): void {
  const blob = new Blob([content], { type: "application/json" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}

/**
 * One button per format the puzzle type declares. The core knows only `native`; every other
 * format, and what it can or cannot express, comes from the type.
 */
export function ExportMenu({ slug, puzzleType }: { slug: string; puzzleType: string }) {
  const { t } = useTranslation();
  const { toast } = useToast();
  const types = usePuzzleTypes();
  const exporter = useExportPuzzle(slug);
  const [lossy, setLossy] = useState<UnsupportedItem[]>([]);
  const formats = (types.data ?? []).find((entry) => entry.key === puzzleType)?.export_formats ?? [
    "native",
  ];

  const run = (format: string) => {
    exporter.mutate(format, {
      onSuccess: (result) => {
        setLossy(result.unsupported ?? []);
        // A type may return its payload as text — either the whole `data`, or a text field
        // alongside its structured data; prefer it over a JSON dump of the same thing.
        const data = result.data as string | { fpuz?: string; text?: string } | null;
        const text =
          typeof data === "string"
            ? data
            : typeof data?.fpuz === "string"
              ? data.fpuz
              : typeof data?.text === "string"
                ? data.text
                : undefined;
        const payload = format === "native" || !text ? JSON.stringify(result.data, null, 2) : text;
        const extension = payload.startsWith("{") ? "json" : "txt";
        // Downloads are unavailable in some sandboxes; failing here must not break the page.
        try {
          download(`${slug}.${format}.${extension}`, payload);
        } catch {
          toast(t("app.error_generic"), "danger");
        }
      },
      onError: () => toast(t("app.error_generic"), "danger"),
    });
  };

  return (
    <div className="bt-export">
      <div className="bt-export__formats">
        {formats.map((format) => (
          <Button
            key={format}
            variant="outline"
            size="sm"
            onClick={() => run(format)}
            loading={exporter.isPending}
          >
            {t(`backlog.export.${format}`, { defaultValue: format })}
          </Button>
        ))}
      </div>
      {lossy.length > 0 ? (
        <div aria-live="polite">
          <Alert tone="danger">{t("backlog.export.lossy", { count: lossy.length })}</Alert>
        </div>
      ) : null}
    </div>
  );
}
